import {
  assessCurrentEnhancementControlValue,
  formatPreparedEnhancementValue,
  prepareEditVehicleEnhancementCandidate,
} from '@/utils/editVehicleEnhancementSource'

export type BulkEnhancementMode = 'fill_missing_or_invalid' | 'replace_eligible'

export type BulkEnhancementFieldSnapshot = {
  field: string
  label: string
  currentValue: unknown
}

export type BulkEnhancementReviewRow = {
  field: string
  label: string
  currentDisplay: string
  chromeDisplay: string
  selected: boolean
}

export type BulkEnhancementReviewResult =
  | {
      status: 'ready'
      identity: string
      rows: BulkEnhancementReviewRow[]
    }
  | {
      status: 'error'
      message: string
    }

export type BulkEnhancementApplyRequest = {
  fields: string[]
  identity: string
}

/**
 * Build bulk enhancement review rows from caller-supplied field snapshots
 * and sanitized Chrome values. Pure: does not read or write Edit Vehicle state.
 */
export function buildEditVehicleBulkEnhancementReview(params: {
  mode: BulkEnhancementMode
  fields: BulkEnhancementFieldSnapshot[]
  sanitizedChromeValues: Record<string, unknown> | null | undefined
  dealerTimeZone?: string
}): BulkEnhancementReviewRow[] {
  const rows: BulkEnhancementReviewRow[] = []

  for (const field of params.fields) {
    const prepared = prepareEditVehicleEnhancementCandidate({
      field: field.field,
      sanitizedValues: params.sanitizedChromeValues,
      dealerTimeZone: params.dealerTimeZone,
    })
    if (!prepared.applicable) continue

    const current = assessCurrentEnhancementControlValue({
      field: field.field,
      value: field.currentValue,
      dealerTimeZone: params.dealerTimeZone,
    })
    if (Object.is(current.normalizedValue, prepared.preparedValue)) continue
    if (params.mode === 'fill_missing_or_invalid' && current.status === 'valid') continue

    rows.push({
      field: field.field,
      label: field.label,
      currentDisplay: current.displayValue,
      chromeDisplay: formatPreparedEnhancementValue(prepared.preparedValue),
      selected: true,
    })
  }

  return rows
}
